// src/services/purchaseLineItemsAPI.ts
// API service for purchase line items

import { authService } from './authService';
import { getApiBaseUrl } from './configService';
import type {
  PurchaseLineItem,
  CreateLineItemRequest,
  UpdateLineItemRequest,
  LineItemsResponse
} from '../types/purchase';

/**
 * Purchase Line Items API Service
 */
export const purchaseLineItemsAPI = {
  /**
   * Create a new line item for a purchase
   */
  async createLineItem(
    purchaseId: string,
    lineItem: CreateLineItemRequest
  ): Promise<PurchaseLineItem> {
    const token = await authService.getToken();
    const baseUrl = getApiBaseUrl();
    
    const response = await fetch(
      `${baseUrl}/purchases/${encodeURIComponent(purchaseId)}/line-items`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(lineItem)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create line item');
    }

    const result = await response.json();
    return result.data;
  },

  /**
   * Get all line items for a purchase
   */
  async getLineItems(purchaseId: string): Promise<LineItemsResponse> {
    const token = await authService.getToken();
    const baseUrl = getApiBaseUrl();
    
    const response = await fetch(
      `${baseUrl}/purchases/${encodeURIComponent(purchaseId)}/line-items`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch line items');
    }

    const result = await response.json();
    return {
      data: result.data || [],
      totals: result.totals || {
        lineItemCount: 0,
        totalCost: 0,
        totalAllocated: 0
      }
    };
  },

  /**
   * Get a single line item
   */
  async getLineItem(
    purchaseId: string,
    lineItemId: string
  ): Promise<PurchaseLineItem> {
    const token = await authService.getToken();
    const baseUrl = getApiBaseUrl();
    
    const response = await fetch(
      `${baseUrl}/purchases/${encodeURIComponent(purchaseId)}/line-items/${encodeURIComponent(lineItemId)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch line item');
    }

    const result = await response.json();
    return result.data;
  },

  /**
   * Update a line item
   */
  async updateLineItem(
    purchaseId: string,
    lineItemId: string,
    updates: UpdateLineItemRequest
  ): Promise<PurchaseLineItem> {
    const token = await authService.getToken();
    const baseUrl = getApiBaseUrl();
    
    const response = await fetch(
      `${baseUrl}/purchases/${encodeURIComponent(purchaseId)}/line-items/${encodeURIComponent(lineItemId)}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update line item');
    }

    const result = await response.json();
    return result.data;
  },

  /**
   * Increment allocated quantity for a line item
   * Called after creating inventory items from this line
   */
  async incrementAllocatedQuantity(
    purchaseId: string,
    lineItemId: string,
    amount: number = 1
  ): Promise<PurchaseLineItem> {
    const token = await authService.getToken();
    const baseUrl = getApiBaseUrl();
    
    const response = await fetch(
      `${baseUrl}/purchases/${encodeURIComponent(purchaseId)}/line-items/${encodeURIComponent(lineItemId)}/increment-allocation`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount })
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to increment allocation');
    }

    const result = await response.json();
    return result.data;
  },

 /**
   * Delete a line item (only if not allocated)
   */
  async deleteLineItem(
    purchaseId: string,
    lineItemId: string
  ): Promise<void> {
    const token = await authService.getToken();
    const baseUrl = getApiBaseUrl();
    
    const response = await fetch(
      `${baseUrl}/purchases/${encodeURIComponent(purchaseId)}/line-items/${encodeURIComponent(lineItemId)}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete line item');
    }
  },

  /**
   * Get all open (not fully allocated) line items - items "on order"
   */
  async getOpenLineItems(productId?: string): Promise<OpenLineItemsResponse> {
    const token = await authService.getToken();
    const baseUrl = getApiBaseUrl();
    
    let url = `${baseUrl}/purchases/open-line-items`;
    if (productId) {
      url += `?productId=${encodeURIComponent(productId)}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch open line items');
    }

    const result = await response.json();
    return { data: result.data || [], count: result.count || 0 };
  },

  /**
   * Get open line items grouped by productId
   */
  async getOpenLineItemsByProduct(): Promise<GroupedOpenLineItemsResponse> {
    const token = await authService.getToken();
    const baseUrl = getApiBaseUrl();
    
    const response = await fetch(
      `${baseUrl}/purchases/open-line-items?groupByProduct=true`,
      {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch grouped open line items');
    }

    const result = await response.json();
    return {
      data: result.data || [],
      summary: result.summary || { totalProducts: 0, totalPendingItems: 0, totalPendingQuantity: 0 }
    };
  },

  /**
   * Check if a specific product has open orders
   */
  async getProductOrderStatus(productId: string): Promise<ProductOrderStatus> {
    const token = await authService.getToken();
    const baseUrl = getApiBaseUrl();
    
    const response = await fetch(
      `${baseUrl}/purchases/open-line-items?productId=${encodeURIComponent(productId)}`,
      {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to check product order status');
    }

    const result = await response.json();
    const items: OpenLineItem[] = result.data || [];
    const totalPending = items.reduce((sum, item) => sum + item.pendingQuantity, 0);

    return {
      hasOpenOrders: items.length > 0,
      totalPendingQuantity: totalPending,
      orders: items.map((item) => ({
        purchaseId: item.purchaseId,
        lineItemId: item.lineItemId,
        itemDescription: item.itemDescription,
        quantity: item.quantity,
        allocatedQuantity: item.allocatedQuantity,
        pendingQuantity: item.pendingQuantity,
        vendor: item.vendor,
        purchaseDate: item.purchaseDate,
        poNumber: item.poNumber
      }))
    };
  }
};

// Types for open line items
export interface OpenLineItem {
  purchaseId: string;
  lineItemId: string;
  tenantId: string;
  productId: string | null;
  itemDescription: string;
  categoryId: string;
  subcategoryId: string;
  quantity: number;
  allocatedQuantity: number;
  pendingQuantity: number;
  unitCost: number;
  totalCost: number;
  vendor?: string;
  purchaseDate?: string;
  poNumber?: string;
}

export interface OpenLineItemsResponse {
  data: OpenLineItem[];
  count: number;
}

export interface ProductOnOrderSummary {
  productId: string | null;
  totalPendingQuantity: number;
  orders: Array<{
    purchaseId: string;
    lineItemId: string;
    itemDescription: string;
    quantity: number;
    allocatedQuantity: number;
    pendingQuantity: number;
    vendor?: string;
    purchaseDate?: string;
    poNumber?: string;
  }>;
}

export interface GroupedOpenLineItemsResponse {
  data: ProductOnOrderSummary[];
  summary: {
    totalProducts: number;
    totalPendingItems: number;
    totalPendingQuantity: number;
  };
}

export interface ProductOrderStatus {
  hasOpenOrders: boolean;
  totalPendingQuantity: number;
  orders: ProductOnOrderSummary['orders'];
}
